import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { navigateToBook } from '@/utils/reading-navigation';

interface Book {
  id: number;
  title: string;
  cover_image_url?: string | null;
  lastPage?: number;
}

interface NetflixThumbnailRowProps {
  title: string;
  books: Book[];
  onBookClick?: (book: Book) => void;
}

export const NetflixThumbnailRow = ({ title, books, onBookClick }: NetflixThumbnailRowProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  if (books.length === 0) return null;

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  const handleBookClick = (book: Book) => {
    if (onBookClick) {
      onBookClick(book);
    } else {
      navigateToBook(Number(book.id), navigate, {
        currentPage: book.lastPage || 0
      });
    }
  };

  return (
    <section className="space-y-3">
      {/* Section Title - uses unified spacing from layout-constants */}
      <h2 className="text-lg font-apple font-semibold text-white tracking-tight px-5 md:px-12 xl:px-16">
        {title}
      </h2>

      {/* Thumbnails Container */}
      <div className="relative group">
        {/* Desktop Navigation Buttons */}
        <button
          onClick={scrollLeft}
          className="hidden md:flex absolute -left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={scrollRight}
          className="hidden md:flex absolute -right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
          aria-label="Scroll right"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Scrollable Row - uses unified spacing from layout-constants */}
        <div
          ref={scrollRef}
          className="overflow-x-auto scrollbar-hide scroll-smooth"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <div className="flex gap-2 pl-5 md:pl-12 xl:pl-16 pr-8 md:pr-16 xl:pr-20 pb-2">
            {books.map((book) => (
              <button
                key={book.id}
                onClick={() => handleBookClick(book)}
                className="flex-shrink-0 group/thumb transition-transform duration-200 hover:scale-105 active:scale-100 focus:outline-none"
              >
                <div className="w-28 sm:w-32 aspect-[2/3] rounded-[8px] overflow-hidden bg-background">
                  <img
                    src={book.cover_image_url || '/placeholder.svg'}
                    alt={book.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </button>
            ))}
            {/* End spacer for right padding in scroll containers
                NOTE: make it a snap point so we can actually scroll into the gap. */}
            <div className="flex-none w-8 md:w-12 xl:w-16 snap-start" aria-hidden="true" />
          </div>
        </div>
      </div>
    </section>
  );
};
